/**
 * Server-side API functions facade for Catalog
 *
 * This file provides a clean interface to all catalog API functions
 * for server-side usage (e.g., in Next.js API routes, server components).
 */
import {
  calorific_patch,
  calorific_post,
  getCalorificPatchUrl,
  getCalorificPostUrl,
} from '../api/calorific';
import {
  catering_get_collection_frontend,
  catering_get_frontend,
  catering_patch,
  catering_post,
  getCateringGetCollectionFrontendUrl,
  getCateringGetFrontendUrl,
  getCateringPatchUrl,
  getCateringPostUrl,
} from '../api/catering';
import {
  diet_patch,
  diet_post,
  get_collection_frontend,
  get_diet_by_slug,
  get_frontend,
  getDietPatchUrl,
  getDietPostUrl,
  getGetCollectionFrontendUrl,
  getGetDietBySlugUrl,
  getGetFrontendUrl,
} from '../api/diet';
import {
  api_discounts_id_delete,
  api_discounts_id_patch,
  api_discounts_id_put,
  api_discounts_post,
  getApiDiscountsIdDeleteUrl,
  getApiDiscountsIdPatchUrl,
  getApiDiscountsIdPutUrl,
  getApiDiscountsPostUrl,
} from '../api/discount';
import {
  getMealTypePatchUrl,
  getMealTypePostUrl,
  mealType_patch,
  mealType_post,
} from '../api/meal-type';
import { get_recommended_diets } from '../api/recommended-diets';
import {
  api_search_result_items_get_collection,
  api_search_result_items_id_delete,
  api_search_result_items_id_get,
  getApiSearchResultItemsGetCollectionUrl,
  getApiSearchResultItemsIdDeleteUrl,
} from '../api/search-result-item';
import {
  getTagDeleteUrl,
  getTagPatchUrl,
  getTagPostUrl,
  get_collection as getTagsCollection,
  getGetCollectionUrl as getTagsGetCollectionUrl,
  tag_delete,
  tag_patch,
  tag_post,
} from '../api/tag';
import {
  getVariantPatchUrl,
  getVariantPostUrl,
  variant_patch,
  variant_post,
} from '../api/variant';
import {
  api_wizard_result_items_get_collection,
  api_wizard_result_items_id_delete,
  api_wizard_result_items_id_patch,
  api_wizard_result_items_id_put,
  api_wizard_result_items_post,
  getApiWizardResultItemsGetCollectionUrl,
  getApiWizardResultItemsIdDeleteUrl,
  getApiWizardResultItemsIdPatchUrl,
  getApiWizardResultItemsIdPutUrl,
  getApiWizardResultItemsPostUrl,
} from '../api/wizard-result-item';

export const catalogApiFacade =
   {
    // Caterings
    caterings: {
      getList: catering_get_collection_frontend,
      getDetails: catering_get_frontend,
      create: catering_post,
      update: catering_patch,
      getListUrl: getCateringGetCollectionFrontendUrl,
      getDetailsUrl: getCateringGetFrontendUrl,
      getCreateUrl: getCateringPostUrl,
      getUpdateUrl: getCateringPatchUrl,
    },

    // Diets
    diets: {
      getList: get_collection_frontend,
      getDetails: get_frontend,
      getBySlug: get_diet_by_slug,
      create: diet_post,
      update: diet_patch,
      getListUrl: getGetCollectionFrontendUrl,
      getDetailsUrl: getGetFrontendUrl,
      getBySlugUrl: getGetDietBySlugUrl,
      getCreateUrl: getDietPostUrl,
      getUpdateUrl: getDietPatchUrl,
    },

    // Tags
    tags: {
      getList: getTagsCollection,
      create: tag_post,
      update: tag_patch,
      delete: tag_delete,
      getListUrl: getTagsGetCollectionUrl,
      getCreateUrl: getTagPostUrl,
      getUpdateUrl: getTagPatchUrl,
      getDeleteUrl: getTagDeleteUrl,
    },

    // Search Result Items
    search: {
      getResults: api_search_result_items_get_collection,
      getResultItemById: api_search_result_items_id_get,
      deleteItem: api_search_result_items_id_delete,
      getResultsUrl: getApiSearchResultItemsGetCollectionUrl,
      getDeleteItemUrl: getApiSearchResultItemsIdDeleteUrl,
    },

    // Wizard Result Items
    wizard: {
      getResults: api_wizard_result_items_get_collection,
      create: api_wizard_result_items_post,
      update: api_wizard_result_items_id_put,
      patch: api_wizard_result_items_id_patch,
      delete: api_wizard_result_items_id_delete,
      getResultsUrl: getApiWizardResultItemsGetCollectionUrl,
      getCreateUrl: getApiWizardResultItemsPostUrl,
      getUpdateUrl: getApiWizardResultItemsIdPutUrl,
      getPatchUrl: getApiWizardResultItemsIdPatchUrl,
      getDeleteUrl: getApiWizardResultItemsIdDeleteUrl,
    },

    // Discounts
    discounts: {
      create: api_discounts_post,
      update: api_discounts_id_put,
      patch: api_discounts_id_patch,
      delete: api_discounts_id_delete,
      getCreateUrl: getApiDiscountsPostUrl,
      getUpdateUrl: getApiDiscountsIdPutUrl,
      getPatchUrl: getApiDiscountsIdPatchUrl,
      getDeleteUrl: getApiDiscountsIdDeleteUrl,
    },

    // Variants
    variants: {
      create: variant_post,
      update: variant_patch,
      getCreateUrl: getVariantPostUrl,
      getUpdateUrl: getVariantPatchUrl,
    },

    // Meal Types
    mealTypes: {
      create: mealType_post,
      update: mealType_patch,
      getCreateUrl: getMealTypePostUrl,
      getUpdateUrl: getMealTypePatchUrl,
    },

    // Calorific
    calorific: {
      create: calorific_post,
      update: calorific_patch,
      getCreateUrl: getCalorificPostUrl,
      getUpdateUrl: getCalorificPatchUrl,
    },

    recommendedDiets: {
      getList: get_recommended_diets,
    },
};

export type CatalogServerApi = typeof catalogApiFacade;
export const catalogApi = catalogApiFacade;
